import React, { useEffect, useRef, useState } from 'react';

import { AudioClip } from './composition';

/** Peaks per second of source kept in memory for drawing. */
const PEAKS_PER_SEC = 50;
const cache = new Map<string, Promise<Float32Array | null>>();

function loadPeaks(src: string): Promise<Float32Array | null> {
    const hit = cache.get(src);
    if (hit) return hit;
    const p = (async () => {
        try {
            const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!AC) return null;
            const res = await fetch(src, { mode: 'cors' });
            if (!res.ok) return null;
            const buf = await res.arrayBuffer();
            const ctx = new AC();
            const audio: AudioBuffer = await new Promise((resolve, reject) => ctx.decodeAudioData(buf, resolve, reject));
            void ctx.close();
            const data = audio.getChannelData(0);
            const per = Math.max(1, Math.floor(audio.sampleRate / PEAKS_PER_SEC));
            const peaks = new Float32Array(Math.ceil(data.length / per));
            for (let i = 0; i < peaks.length; i++) {
                let max = 0;
                const end = Math.min(data.length, (i + 1) * per);
                for (let j = i * per; j < end; j += 4) {
                    const v = Math.abs(data[j]);
                    if (v > max) max = v;
                }
                peaks[i] = max;
            }
            return peaks;
        } catch {
            return null;
        }
    })();
    cache.set(src, p);
    return p;
}

/** The waveform of the part of the source an audio clip plays. */
export const AudioWaveform: React.FC<{ clip: AudioClip; width: number; height: number }> = ({ clip, width, height }) => {
    const ref = useRef<HTMLCanvasElement>(null);
    const [peaks, setPeaks] = useState<Float32Array | null>(null);

    useEffect(() => {
        let alive = true;
        void loadPeaks(clip.src).then((p) => { if (alive) setPeaks(p); });
        return () => { alive = false; };
    }, [clip.src]);

    const w = Math.min(4000, Math.max(4, Math.round(width)));
    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, w, height);
        const color = getComputedStyle(canvas).color || '#888';
        ctx.fillStyle = color;
        const mid = height / 2 + 4;
        const amp = height / 2 - 8;
        const bar = 2;
        const gap = 1;
        for (let x = 0; x < w; x += bar + gap) {
            let v: number;
            if (peaks) {
                const t = clip.trimIn + (x / w) * clip.duration;
                v = peaks[Math.min(peaks.length - 1, Math.floor(t * PEAKS_PER_SEC))] ?? 0;
            } else {
                // A quiet placeholder until the file is decoded.
                v = 0.15 + 0.1 * Math.abs(Math.sin(x * 0.13));
            }
            const h = Math.max(1, Math.min(1, v * 1.4) * amp);
            ctx.fillRect(x, mid - h, bar, h * 2);
        }
    }, [peaks, w, height, clip.trimIn, clip.duration]);

    return <canvas ref={ref} width={w} height={height} className="pointer-events-none absolute inset-0 text-muted-foreground/60" style={{ width: w, height }} />;
};
